// src/components/Filiacao/ConfirmacaoFiliacao.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CheckCircle, Clock, Mail, Phone, FileText, Copy, Check, Home, Building2, User } from 'lucide-react';

const BRAND = {
  gold: '#D4AF37',
  goldDeep: '#B88917',
  charcoal: '#1F2328',
};

interface ConfirmacaoFiliacaoProps {
  dados: {
    etapa1: {
      nomeSolicitante: string;
      cpf: string;            // obrigatório
      cnpj: string;           // opcional (string vazia = não informado)
      nomeFantasia: string;   // opcional
      cidade: string;
      estado: string;
      email: string;
      telefone: string;
      endereco: string;
      cep: string;
      complemento: string;
    };
    etapa2: {
      funcao: string;
      dataInicioAtividade: string;
      experienciaAnos: number;
      especializacoes: string;
    };
    documentos: File[];
  };
  filiacaoId: string;
}

const ConfirmacaoFiliacao: React.FC<ConfirmacaoFiliacaoProps> = ({ dados, filiacaoId }) => {
  const [copiado, setCopiado] = useState(false);
  const navigate = useNavigate();

  const handleCopiarID = async () => {
    try {
      await navigator.clipboard.writeText(filiacaoId);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      // sem barulho: se clipboard falhar, não quebra a UI
    }
  };

const idFormatado = filiacaoId;

  const temCnpj = Boolean(dados.etapa1.cnpj?.trim());
  const temNomeFantasia = Boolean(dados.etapa1.nomeFantasia?.trim());
  const temTelefone = Boolean(dados.etapa1.telefone?.trim());
  const temFuncao = Boolean(dados.etapa2.funcao?.trim());
  const temInicio = Boolean(dados.etapa2.dataInicioAtividade?.trim());

  return (
    <div className="space-y-6">
      {/* Sucesso */}
      <div
        className="rounded-3xl border bg-white p-8 text-center shadow-sm"
        style={{ borderColor: `${BRAND.gold}33` }}
      >
        <div
          className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${BRAND.gold}1A` }}
        >
          <CheckCircle className="w-10 h-10" style={{ color: BRAND.goldDeep }} />
        </div>

        <h3 className="text-2xl md:text-3xl font-black mt-4" style={{ color: BRAND.charcoal }}>
          Filiação enviada com sucesso
        </h3>
        <p className="text-gray-600 mt-2">Seu cadastro foi registrado e está aguardando análise.</p>

        {/* Protocolo */}
        <div
          className="mt-6 rounded-2xl border p-5"
          style={{ borderColor: `${BRAND.gold}33`, backgroundColor: `${BRAND.gold}0F` }}
        >
          <p className="text-xs font-bold tracking-wide uppercase" style={{ color: BRAND.charcoal }}>
            Protocolo da Filiação
          </p>

          <div className="mt-2 flex items-center justify-center gap-3">
            <p className="text-2xl md:text-3xl font-mono font-black" style={{ color: BRAND.charcoal }}>
              {idFormatado}
            </p>

            <button
              type="button"
              onClick={handleCopiarID}
              className="p-2 rounded-xl border hover:opacity-90 transition"
              style={{ borderColor: `${BRAND.gold}55`, backgroundColor: 'white' }}
              aria-label="Copiar protocolo"
            >
              {copiado ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Copy className="w-5 h-5" style={{ color: BRAND.charcoal }} />
              )}
            </button>
          </div>

          <p className="text-xs text-gray-700 mt-2">
            Guarde este protocolo. Ele será usado para acompanhar sua solicitação.
          </p>
          <button
  type="button"
  onClick={() => navigate(`/verificar/${encodeURIComponent(filiacaoId)}`)}
  className="mt-4 w-full py-4 rounded-2xl font-black text-white hover:opacity-90 transition"
  style={{ backgroundColor: BRAND.charcoal }}
>
  Acompanhar Status da Solicitação →
</button>
        </div>
      </div>

      {/* Próximas etapas */}
      <div
        className="rounded-3xl border bg-white p-6 shadow-sm"
        style={{ borderColor: `${BRAND.gold}33` }}
      >
        <h4 className="text-lg font-black" style={{ color: BRAND.charcoal }}>
          Próximas etapas
        </h4>

        <div className="mt-4 space-y-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-2xl" style={{ backgroundColor: `${BRAND.gold}1A` }}>
              <Clock className="w-6 h-6" style={{ color: BRAND.goldDeep }} />
            </div>
            <div>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                1. Análise de documentos
              </p>
              <p className="text-sm text-gray-600">Estimativa: 1–3 dias úteis.</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-3 rounded-2xl" style={{ backgroundColor: `${BRAND.charcoal}0D` }}>
              <Mail className="w-6 h-6" style={{ color: BRAND.charcoal }} />
            </div>
            <div>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                2. Notificação por e-mail
              </p>
              <p className="text-sm text-gray-600">Enviado para: {dados.etapa1.email}</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-3 rounded-2xl" style={{ backgroundColor: `${BRAND.gold}1A` }}>
              <CheckCircle className="w-6 h-6" style={{ color: BRAND.goldDeep }} />
            </div>
            <div>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                3. Acesso ao portal
              </p>
              <p className="text-sm text-gray-600">Após aprovação, você recebe as credenciais.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Identificação */}
        <div
          className="rounded-3xl border bg-white p-6 shadow-sm"
          style={{ borderColor: `${BRAND.gold}33` }}
        >
          <h5 className="text-base font-black flex items-center gap-2" style={{ color: BRAND.charcoal }}>
            <User className="w-5 h-5" style={{ color: BRAND.goldDeep }} />
            Identificação
          </h5>

          <div className="mt-4 space-y-3 text-sm">
            <div>
              <p className="text-gray-600">Nome</p>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                {dados.etapa1.nomeSolicitante}
              </p>
            </div>

            <div>
              <p className="text-gray-600">CPF</p>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                {dados.etapa1.cpf}
              </p>
            </div>

            {temCnpj && (
              <div>
                <p className="text-gray-600">CNPJ</p>
                <p className="font-bold" style={{ color: BRAND.charcoal }}>
                  {dados.etapa1.cnpj}
                </p>
              </div>
            )}

            {temNomeFantasia && (
              <div>
                <p className="text-gray-600">Nome fantasia</p>
                <p className="font-bold" style={{ color: BRAND.charcoal }}>
                  {dados.etapa1.nomeFantasia}
                </p>
              </div>
            )}

            <div>
              <p className="text-gray-600">E-mail</p>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                {dados.etapa1.email}
              </p>
            </div>

            {temTelefone && (
              <div>
                <p className="text-gray-600">Telefone</p>
                <p className="font-bold" style={{ color: BRAND.charcoal }}>
                  {dados.etapa1.telefone}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Endereço / Complementar */}
        <div
          className="rounded-3xl border bg-white p-6 shadow-sm"
          style={{ borderColor: `${BRAND.gold}33` }}
        >
          <h5 className="text-base font-black flex items-center gap-2" style={{ color: BRAND.charcoal }}>
            <Building2 className="w-5 h-5" style={{ color: BRAND.goldDeep }} />
            Informações complementares
          </h5>

          <div className="mt-4 space-y-3 text-sm">
            <div>
              <p className="text-gray-600">Localização</p>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                {dados.etapa1.cidade}, {dados.etapa1.estado}
              </p>
            </div>

            <div>
              <p className="text-gray-600">Endereço</p>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                {dados.etapa1.endereco}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                CEP: {dados.etapa1.cep}
                {dados.etapa1.complemento?.trim() ? ` • ${dados.etapa1.complemento}` : ''}
              </p>
            </div>

            {temFuncao && (
              <div>
                <p className="text-gray-600">Atuação/Função</p>
                <p className="font-bold" style={{ color: BRAND.charcoal }}>
                  {dados.etapa2.funcao}
                </p>
              </div>
            )}

            <div>
              <p className="text-gray-600">Experiência</p>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                {dados.etapa2.experienciaAnos} anos
              </p>
            </div>

            {temInicio && (
              <div>
                <p className="text-gray-600">Início na área</p>
                <p className="font-bold" style={{ color: BRAND.charcoal }}>
                  {dados.etapa2.dataInicioAtividade}
                </p>
              </div>
            )}

            <div>
              <p className="text-gray-600">Especializações</p>
              <p className="font-bold" style={{ color: BRAND.charcoal }}>
                {dados.etapa2.especializacoes}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Docs */}
      <div
        className="rounded-3xl border bg-white p-6 shadow-sm"
        style={{ borderColor: `${BRAND.gold}33` }}
      >
        <h5 className="text-base font-black" style={{ color: BRAND.charcoal }}>
          Documentos enviados
        </h5>

        <div className="mt-4 space-y-2">
          {dados.documentos.map((doc, idx) => (
            <div
              key={`${doc.name}-${doc.size}-${idx}`}
              className="flex items-center gap-3 p-3 rounded-2xl"
              style={{ backgroundColor: `${BRAND.charcoal}07` }}
            >
              <FileText className="w-5 h-5" style={{ color: BRAND.charcoal }} />
              <div className="flex-1">
                <p className="text-sm font-bold" style={{ color: BRAND.charcoal }}>
                  {doc.name}
                </p>
                <p className="text-xs text-gray-600">{(doc.size / 1024).toFixed(2)} KB</p>
              </div>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          ))}
        </div>
      </div>

      {/* Contato + voltar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div
          className="flex-1 rounded-3xl border p-6"
          style={{ borderColor: `${BRAND.gold}33`, backgroundColor: `${BRAND.gold}0F` }}
        >
          <p className="font-black" style={{ color: BRAND.charcoal }}>
            Precisa de ajuda?
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5" style={{ color: BRAND.charcoal }} />
              <div>
                <p className="font-bold" style={{ color: BRAND.charcoal }}>
                  Suporte
                </p>
                <p className="text-gray-700">(11) 99999-9999</p>
                <p className="text-xs text-gray-600">WhatsApp</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5" style={{ color: BRAND.charcoal }} />
              <div>
                <p className="font-bold" style={{ color: BRAND.charcoal }}>
                  E-mail
                </p>
                <p className="text-gray-700">suporte@federacao.com.br</p>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => (window.location.href = '/')}
          className="md:w-[240px] px-6 py-4 rounded-2xl font-black flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition"
          style={{ backgroundColor: BRAND.charcoal, color: 'white' }}
        >
          <Home className="w-5 h-5" />
          Voltar ao início
        </button>
      </div>
    </div>
  );
};

export default ConfirmacaoFiliacao;
